import '../style.css';
import {Map as OLMap, View} from 'ol';
import TileLayer from 'ol/layer/Tile';
import OSM from 'ol/source/OSM';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Point from 'ol/geom/Point';
import LineString from 'ol/geom/LineString';
import { fromLonLat } from 'ol/proj';

// Utilities
import { getByName, normalizeName } from './utils';

// Styles
import { getPrimaryPointStyle, getReferencedPointStyle, getRouteStyles, sharedConnectionActiveStyle } from './styles';

// Data loading
import { loadLocations, loadReferencedLocations, loadThemes, loadPeople } from './loaders';

// Map interactions
import { focusOnFeature, zoomToWalkedLocations, zoomToWholeWorld, zoomToLinkedLocations, fitInitialRouteView } from './interactions';

// Navigation
import { createNavigationState, pushNavigationHistory, clearNavigationHistory, goBack, goForward, goToHistoryIndex } from './navigation';

// UI rendering
import { renderBrowseMenu, renderInfoLanding, renderFeatureInfo, renderEntityInfo } from './ui';

// DOM elements
const infoEl = document.getElementById('info-content');
const zoomWalkedEl = document.getElementById('zoom-walked');
const zoomWorldEl = document.getElementById('zoom-world');

// Map setup
const map = new OLMap({
  target: 'map',
  layers: [
    new TileLayer({
      source: new OSM()
    })
  ],
  view: new View({
    center: fromLonLat([20, 40]),
    zoom: 3
  })
});

// Data sources
const vectorSource = new VectorSource();
const referencedVectorSource = new VectorSource();
const connectionVectorSource = new VectorSource();

// Entity stores
const locationsByName = {};
const referencedLocationsByName = {};
const themesByName = {};
const peopleByName = {};

// Feature stores
const locationFeaturesByName = {};
const referencedFeaturesByName = {};

// State management
let activeLocationName = null;
let activeReferencedLocationName = null;
let isZoomedToLinked = false;

const navigationState = createNavigationState();

// Vector layers
const vectorLayer = new VectorLayer({
  source: vectorSource,
  style: function(feature) {
    const geometry = feature.getGeometry();
    if (geometry instanceof Point) {
      return getPrimaryPointStyle(feature.get('name'));
    } else if (geometry instanceof LineString) {
      if (!feature.get('routeSegment')) {
        return null;
      }
      return getRouteStyles(feature.get('routeLabel'));
    }
  }
});

const referencedVectorLayer = new VectorLayer({
  source: referencedVectorSource,
  style: function(feature) {
    let isHighlighted = false;
    const featureName = feature.get('name');
    
    if (activeLocationName !== null) {
      const activeLocationFeature = getByName(locationFeaturesByName, activeLocationName);
      if (activeLocationFeature && activeLocationFeature.feature) {
        const relatedReferencedLocations = activeLocationFeature.feature.get('relatedReferencedLocations') || [];
        isHighlighted = relatedReferencedLocations.some(name => normalizeName(name) === normalizeName(featureName));
      }
    } else if (activeReferencedLocationName !== null) {
      if (normalizeName(featureName) === normalizeName(activeReferencedLocationName)) {
        isHighlighted = true;
      } else {
        const activeReferencedFeature = getByName(referencedFeaturesByName, activeReferencedLocationName);
        if (activeReferencedFeature && activeReferencedFeature.feature) {
          const relatedReferencedLocations = activeReferencedFeature.feature.get('relatedReferencedLocations') || [];
          isHighlighted = relatedReferencedLocations.some(name => normalizeName(name) === normalizeName(featureName));
        }
      }
    }
    return getReferencedPointStyle(featureName, isHighlighted);
  }
});

const connectionVectorLayer = new VectorLayer({
  source: connectionVectorSource,
  style: function(feature) {
    const isActive =
      (activeLocationName !== null && feature.get('locationName') === activeLocationName) ||
      (activeReferencedLocationName !== null && feature.get('referencedLocationName') === activeReferencedLocationName);
    if (isActive) {
      return sharedConnectionActiveStyle;
    }
    return null;
  }
});

// Add layers to map
map.addLayer(referencedVectorLayer);
map.addLayer(connectionVectorLayer);
map.addLayer(vectorLayer);

// Load data
async function initializeApp() {
  await Promise.all([
    loadReferencedLocations(referencedVectorSource, referencedLocationsByName, referencedFeaturesByName),
    loadThemes(themesByName),
    loadPeople(peopleByName)
  ]);

  const orderedRouteEntries = await loadLocations(
    vectorSource,
    connectionVectorSource,
    locationsByName,
    locationFeaturesByName,
    referencedLocationsByName,
    referencedFeaturesByName
  );

  fitInitialRouteView(map, orderedRouteEntries);
  zoomToWalkedLocations(map, vectorSource);

  // Event listeners
  setupEventListeners();
  
  renderInfoLanding(infoEl, navigationState);
}

function setupEventListeners() {
  // Map click
  map.on('click', function(evt) {
    const feature = map.forEachFeatureAtPixel(evt.pixel, function(feature) {
      return feature;
    });
    if (feature && feature.getGeometry() instanceof Point) {
      const entityType = feature.get('type') || 'location';
      openEntity(entityType, feature.get('name'), {
        shouldPan: false,
        pushHistory: true,
        zoomToLinked: entityType === 'location'
      });
    } else {
      activeLocationName = null;
      activeReferencedLocationName = null;
      isZoomedToLinked = false;
      connectionVectorLayer.changed();
      referencedVectorLayer.changed();
    }
  });

  // Info panel clicks
  infoEl.addEventListener('click', handleInfoPanelClick);

  // Zoom buttons
  zoomWalkedEl.addEventListener('click', () => zoomToWalkedLocations(map, vectorSource));
  zoomWorldEl.addEventListener('click', () => zoomToWholeWorld(map));
}

function handleInfoPanelClick(event) {
  const browseBtn = event.target.closest('#open-browse');
  if (browseBtn) {
    activeLocationName = null;
    activeReferencedLocationName = null;
    isZoomedToLinked = false;
    connectionVectorLayer.changed();
    referencedVectorLayer.changed();
    renderBrowseMenu(infoEl, navigationState, locationsByName, referencedLocationsByName, themesByName, peopleByName);
    return;
  }

  const clearBtn = event.target.closest('#clear-history');
  if (clearBtn) {
    activeLocationName = null;
    activeReferencedLocationName = null;
    isZoomedToLinked = false;
    clearNavigationHistory(navigationState, connectionVectorLayer, referencedVectorLayer);
    renderInfoLanding(infoEl, navigationState);
    return;
  }

  const navButton = event.target.closest('.history-nav');
  if (navButton) {
    const direction = navButton.getAttribute('data-direction');
    let entry = null;
    if (direction === 'back') {
      entry = goBack(navigationState);
    } else if (direction === 'forward') {
      entry = goForward(navigationState);
    }
    if (entry) {
      openEntity(entry.entityType, entry.entityName, { shouldPan: true, pushHistory: false });
    }
    return;
  }

  const breadcrumbLink = event.target.closest('.breadcrumb-link');
  if (breadcrumbLink) {
    const targetIndex = Number(breadcrumbLink.getAttribute('data-history-index'));
    const entry = goToHistoryIndex(navigationState, targetIndex);
    if (entry) {
      openEntity(entry.entityType, entry.entityName, { shouldPan: true, pushHistory: false });
    }
    return;
  }

  const browseSummary = event.target.closest('.browse-group summary');
  if (browseSummary) {
    const details = browseSummary.parentElement;
    if (details) {
      navigationState.lastBrowseCategory = details.getAttribute('data-browse-category');
      const openGroups = infoEl.querySelectorAll('.browse-group[open]');
      openGroups.forEach((group) => {
        if (group !== details) {
          group.removeAttribute('open');
        }
      });
    }
    return;
  }

  const zoomToggleBtn = event.target.closest('#zoom-toggle');
  if (zoomToggleBtn) {
    if (activeLocationName !== null) {
      const activeLocationFeature = getByName(locationFeaturesByName, activeLocationName);
      if (activeLocationFeature && activeLocationFeature.feature) {
        isZoomedToLinked = !isZoomedToLinked;
        if (isZoomedToLinked) {
          zoomToLinkedLocations(map, activeLocationFeature.feature, locationFeaturesByName, referencedFeaturesByName, getByName);
        } else {
          focusOnFeature(map, activeLocationFeature.feature);
        }
        renderFeatureInfo(infoEl, navigationState, activeLocationFeature.feature, { shouldPan: false }, locationFeaturesByName, referencedFeaturesByName, zoomToLinkedLocations, focusOnFeature, connectionVectorLayer, referencedVectorLayer, isZoomedToLinked);
      }
    }
    return;
  }

  const linkButton = event.target.closest('.entity-link');
  if (!linkButton) {
    return;
  }

  const browseGroup = linkButton.closest('.browse-group');
  if (browseGroup) {
    navigationState.lastBrowseCategory = browseGroup.getAttribute('data-browse-category');
  }

  const entityType = linkButton.getAttribute('data-entity-type');
  const encodedName = linkButton.getAttribute('data-entity-name') || '';
  const entityName = decodeURIComponent(encodedName);
  const isBrowseClick = !!linkButton.closest('.browse-group');
  if (isBrowseClick && entityType === 'location') {
    openEntity(entityType, entityName, {
      shouldPan: true,
      pushHistory: true,
      zoomToLinked: true
    });
    return;
  }

  openEntity(entityType, entityName, { shouldPan: true, pushHistory: true });
}

function openEntity(entityType, entityName, options = {}) {
  const { shouldPan = true, pushHistory = true, zoomToLinked = false } = options;
  const shouldZoomToLinked = entityType === 'location' ? true : zoomToLinked;
  
  if (pushHistory) {
    pushNavigationHistory(navigationState, entityType, entityName);
  }

  if (entityType === 'location') {
    const featureEntry = getByName(locationFeaturesByName, entityName);
    if (featureEntry) {
      activeLocationName = entityName;
      activeReferencedLocationName = null;

      if (shouldZoomToLinked) {
        zoomToLinkedLocations(map, featureEntry.feature, locationFeaturesByName, referencedFeaturesByName, getByName);
        isZoomedToLinked = true;
      } else if (shouldPan) {
        focusOnFeature(map, featureEntry.feature);
        isZoomedToLinked = false;
      }

      connectionVectorLayer.changed();
      referencedVectorLayer.changed();
      renderFeatureInfo(infoEl, navigationState, featureEntry.feature, { shouldPan: false }, locationFeaturesByName, referencedFeaturesByName, zoomToLinkedLocations, focusOnFeature, connectionVectorLayer, referencedVectorLayer, isZoomedToLinked);
    }
    return;
  }

  if (entityType === 'referencedLocation') {
    const featureEntry = getByName(referencedFeaturesByName, entityName);
    if (featureEntry) {
      activeLocationName = null;
      activeReferencedLocationName = entityName;
      isZoomedToLinked = false;
      connectionVectorLayer.changed();
      referencedVectorLayer.changed();
      if (shouldPan) {
        focusOnFeature(map, featureEntry.feature);
      }
      renderFeatureInfo(infoEl, navigationState, featureEntry.feature, { shouldPan: false }, locationFeaturesByName, referencedFeaturesByName, zoomToLinkedLocations, focusOnFeature, connectionVectorLayer, referencedVectorLayer, isZoomedToLinked);
    }
    return;
  }

  if (entityType === 'theme') {
    const theme = getByName(themesByName, entityName);
    if (theme) {
      activeLocationName = null;
      activeReferencedLocationName = null;
      isZoomedToLinked = false;
      connectionVectorLayer.changed();
      referencedVectorLayer.changed();
      renderEntityInfo(infoEl, navigationState, theme, 'theme');
    }
    return;
  }

  if (entityType === 'person') {
    const person = getByName(peopleByName, entityName);
    if (person) {
      activeLocationName = null;
      activeReferencedLocationName = null;
      isZoomedToLinked = false;
      connectionVectorLayer.changed();
      referencedVectorLayer.changed();
      renderEntityInfo(infoEl, navigationState, person, 'person');
    }
  }
}

// Initialize the application
initializeApp();
